import { errorToObject } from '../util/serialize.js';

export interface GlobalErrorEvent {
  source: 'global-error' | 'unhandled-rejection';
  message: string;
  errorName?: string;
  errorMessage?: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
}

function buildLocation(file?: string, line?: number, col?: number): string {
  let s = '';
  if (file) s += file;
  if (line !== undefined) s += `:${line}`;
  if (col !== undefined) s += `:${col}`;
  return s;
}

export interface GlobalErrorTarget {
  addEventListener: (type: string, listener: EventListenerOrEventListenerObject, options?: AddEventListenerOptions | boolean) => void;
  removeEventListener: (type: string, listener: EventListenerOrEventListenerObject, options?: EventListenerOptions | boolean) => void;
}

export interface InstallGlobalHandle {
  uninstall: () => void;
}

export function installGlobalHandlers(
  target: GlobalErrorTarget,
  onCapture: (event: GlobalErrorEvent) => void,
  runGuarded: <T>(fn: () => T) => T,
  isGuardActive: () => boolean,
): InstallGlobalHandle {
  const errorListener: EventListener = (rawEvent) => {
    if (isGuardActive()) return;
    const ev = rawEvent as ErrorEvent;

    const file =
      typeof ev.filename === 'string' && ev.filename.length > 0 ? ev.filename : undefined;
    const line = typeof ev.lineno === 'number' && ev.lineno > 0 ? ev.lineno : undefined;
    const col = typeof ev.colno === 'number' && ev.colno > 0 ? ev.colno : undefined;

    let baseMsg = ev.message;
    if (!baseMsg) {
      if (ev.error instanceof Error) {
        const name = ev.error.name || 'Error';
        const msg = ev.error.message || '';
        baseMsg = msg ? `${name}: ${msg}` : name;
      } else {
        baseMsg = 'Uncaught error';
      }
    }

    const loc = buildLocation(file, line, col);
    const message = loc ? `${baseMsg} at ${loc}` : baseMsg;

    const out: GlobalErrorEvent = {
      source: 'global-error',
      message,
    };
    if (file) out.filename = file;
    if (line !== undefined) out.lineno = line;
    if (col !== undefined) out.colno = col;

    if (ev.error instanceof Error) {
      const e = errorToObject(ev.error);
      if (e.name) out.errorName = e.name;
      if (e.message) out.errorMessage = e.message;
      if (e.stack) out.stack = e.stack;
    }

    try {
      runGuarded(() => onCapture(out));
    } catch {
      /* swallow */
    }
  };

  const rejectionListener: EventListener = (rawEvent) => {
    if (isGuardActive()) return;
    const ev = rawEvent as PromiseRejectionEvent;
    const reason = ev.reason;
    const out: GlobalErrorEvent = {
      source: 'unhandled-rejection',
      message: 'Unhandled promise rejection',
    };
    if (reason instanceof Error) {
      const e = errorToObject(reason);
      if (e.name) out.errorName = e.name;
      if (e.message) out.errorMessage = e.message;
      if (e.stack) out.stack = e.stack;
      const name = e.name || 'Error';
      out.message = e.message ? `${name}: ${e.message}` : name;
    } else if (typeof reason === 'string') {
      out.message = reason;
      out.errorMessage = reason;
    } else if (reason !== undefined && reason !== null) {
      try {
        out.message = JSON.stringify(reason) ?? String(reason);
      } catch {
        out.message = String(reason);
      }
      out.errorMessage = out.message;
    }
    try {
      runGuarded(() => onCapture(out));
    } catch {
      /* swallow */
    }
  };

  target.addEventListener('error', errorListener, true);
  target.addEventListener('unhandledrejection', rejectionListener);

  return {
    uninstall: () => {
      target.removeEventListener('error', errorListener, true);
      target.removeEventListener('unhandledrejection', rejectionListener);
    },
  };
}
